import { useEffect, useState } from "react";
import api, { errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { BookOpen, MessageSquareText, Plus, Trash2, Copy, Check, Loader2, Wand2, Pencil, X } from "lucide-react";

async function copyText(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text || ""); return true; }
    throw new Error();
  } catch {
    try {
      const ta = document.createElement("textarea"); ta.value = text || ""; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.focus(); ta.select(); document.execCommand("copy"); document.body.removeChild(ta); return true;
    } catch { return false; }
  }
}

export default function Playbook() {
  const [tab, setTab] = useState("pitches");
  return (
    <div className="p-6 max-w-4xl">
      <h1 className="font-head text-2xl font-black tracking-tight flex items-center gap-2"><BookOpen className="h-6 w-6" /> Playbook</h1>
      <p className="text-xs text-zinc-500 font-mono mb-5">Shared pitches from your team, and an AI helper for what to reply on LinkedIn</p>

      <div className="flex gap-1 bg-zinc-100 rounded-xl p-1 w-fit mb-5">
        {[["pitches", "Pitches"], ["reply", "Reply Assistant"]].map(([v, l]) => (
          <button key={v} data-testid={`playbook-tab-${v}`} onClick={() => setTab(v)}
            className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wide rounded-lg transition-all ${tab === v ? "bg-white text-klein shadow-sm" : "text-zinc-500 hover:text-zinc-900"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "pitches" ? <Pitches /> : <ReplyAssistant />}
    </div>
  );
}

function Pitches() {
  const { user } = useAuth();
  const canEdit = user.role === "admin" || user.role === "bde";
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", tags: "", content: "" });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const load = () => api.get("/pitches").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reset = () => { setForm({ title: "", tags: "", content: "" }); setEditId(null); };
  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error("Title and content are required"); return; }
    setSaving(true);
    try {
      if (editId) await api.patch(`/pitches/${editId}`, form);
      else await api.post("/pitches", form);
      toast.success(editId ? "Pitch updated" : "Pitch shared with the team"); reset(); load();
    } catch (e) { toast.error(errText(e)); } finally { setSaving(false); }
  };
  const edit = (p) => { setForm({ title: p.title, tags: p.tags || "", content: p.content }); setEditId(p.id); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (p) => { try { await api.delete(`/pitches/${p.id}`); load(); toast.success("Deleted"); } catch (e) { toast.error(errText(e)); } };
  const copy = async (p) => { const ok = await copyText(p.content); if (ok) { setCopiedId(p.id); setTimeout(() => setCopiedId(null), 1200); toast.success("Copied"); } };

  return (
    <div>
      {canEdit && (
        <div className="bg-white border-2 border-black rounded-xl p-4 mb-5" data-testid="pitch-form">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-bold uppercase tracking-wider">{editId ? "Edit pitch" : "Share a new pitch"}</div>
            {editId && <button onClick={reset} className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1"><X className="h-3 w-3" /> cancel edit</button>}
          </div>
          <div className="grid md:grid-cols-2 gap-2 mb-2">
            <input data-testid="pitch-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (e.g. Founder feature — cold opener)"
              className="border border-zinc-300 rounded-lg px-2.5 py-2 text-sm focus:border-klein focus:outline-none" />
            <input data-testid="pitch-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Tags (optional, e.g. cold, fintech)"
              className="border border-zinc-300 rounded-lg px-2.5 py-2 text-sm focus:border-klein focus:outline-none" />
          </div>
          <textarea data-testid="pitch-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={5}
            placeholder="The pitch / script the team should use…"
            className="w-full border border-zinc-300 rounded-lg p-2.5 text-sm resize-none focus:border-klein focus:outline-none" />
          <button data-testid="save-pitch" onClick={save} disabled={saving}
            className="mt-2 flex items-center gap-2 bg-klein hover:bg-klein-hover text-white px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {editId ? "Save changes" : "Share pitch"}
          </button>
        </div>
      )}

      <div className="space-y-2.5" data-testid="pitch-list">
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-zinc-200 rounded-xl p-4" data-testid={`pitch-${p.id}`}>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm">{p.title}</span>
              {p.tags && <span className="text-[10px] font-mono uppercase bg-zinc-100 text-zinc-600 px-1.5 py-0.5 rounded">{p.tags}</span>}
              <div className="ml-auto flex items-center gap-2">
                <button data-testid={`copy-pitch-${p.id}`} onClick={() => copy(p)} className="text-zinc-500 hover:text-klein transition-colors">
                  {copiedId === p.id ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
                </button>
                {canEdit && <button data-testid={`edit-pitch-${p.id}`} onClick={() => edit(p)} className="text-zinc-500 hover:text-klein transition-colors"><Pencil className="h-4 w-4" /></button>}
                {canEdit && <button data-testid={`delete-pitch-${p.id}`} onClick={() => del(p)} className="text-zinc-400 hover:text-red-600 transition-colors"><Trash2 className="h-4 w-4" /></button>}
              </div>
            </div>
            <p className="text-sm text-zinc-600 mt-1.5 whitespace-pre-wrap leading-relaxed">{p.content}</p>
            <div className="text-[10px] text-zinc-400 font-mono mt-2">by {p.created_by_name}</div>
          </div>
        ))}
        {items.length === 0 && <div className="text-center text-zinc-400 font-mono py-12 text-xs border border-dashed border-zinc-300 rounded-xl">No pitches yet{canEdit ? " — share your first one above." : ". Your admin/BDE will add some."}</div>}
      </div>
    </div>
  );
}

function ReplyAssistant() {
  const [conversation, setConversation] = useState("");
  const [context, setContext] = useState("");
  const [channel, setChannel] = useState("LinkedIn");
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const gen = async () => {
    if (!conversation.trim()) { toast.error("Paste the message you received first"); return; }
    setBusy(true);
    try { const { data } = await api.post("/reply-suggest", { conversation, context, channel }); setOut(data.reply); toast.success("Suggested reply ready"); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const copy = async () => { const ok = await copyText(out); if (ok) { setCopied(true); setTimeout(() => setCopied(false), 1200); toast.success("Copied"); } };

  return (
    <div className="space-y-4" data-testid="reply-assistant">
      <p className="text-xs text-zinc-500">Not sure how to reply on LinkedIn? Paste their message and get a suggested reply based on our offers, pitch and tone.</p>
      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Paste the conversation / their message</label>
        <textarea data-testid="reply-conversation" value={conversation} onChange={(e) => setConversation(e.target.value)} rows={6}
          placeholder={"Them: Hi, thanks for connecting! What exactly do you guys do?"}
          className="w-full border border-zinc-300 rounded-lg p-3 text-sm resize-none focus:border-klein focus:ring-2 focus:ring-klein/20 focus:outline-none" />
      </div>
      <div className="flex gap-2">
        <input data-testid="reply-context" value={context} onChange={(e) => setContext(e.target.value)} placeholder="About them (optional): D2C founder, 8k followers…"
          className="flex-1 border border-zinc-300 rounded-lg px-2.5 py-2 text-sm focus:border-klein focus:outline-none" />
        <select data-testid="reply-channel" value={channel} onChange={(e) => setChannel(e.target.value)} className="border border-zinc-300 rounded-lg px-2 py-2 text-sm focus:border-klein focus:outline-none">
          {["LinkedIn", "WhatsApp", "Email"].map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>
      <button data-testid="suggest-reply-btn" onClick={gen} disabled={busy}
        className="w-full bg-klein hover:bg-klein-hover text-white text-sm font-medium py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} {out ? "Regenerate reply" : "Suggest a reply"}
      </button>

      {out && (
        <div className="border border-zinc-300 rounded-lg bg-white" data-testid="reply-output">
          <div className="flex items-center justify-between px-3 py-1.5 bg-zinc-100 border-b border-zinc-300 rounded-t-lg">
            <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-600 flex items-center gap-1.5"><MessageSquareText className="h-3.5 w-3.5" /> Suggested reply · editable</span>
            <button data-testid="reply-copy" onClick={copy} className="flex items-center gap-1 text-xs font-medium text-zinc-600 hover:text-klein transition-colors">
              {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />} Copy
            </button>
          </div>
          <textarea data-testid="reply-text" value={out} onChange={(e) => setOut(e.target.value)} rows={Math.min(14, Math.max(4, Math.ceil(out.length / 48)))}
            className="w-full p-3 text-sm resize-none focus:outline-none leading-relaxed rounded-b-lg" />
        </div>
      )}
    </div>
  );
}
